import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  CompareArrows,
  Send,
  MoreHoriz,
  Refresh,
  WarningAmber,
  InsertDriveFileOutlined,
  AutoAwesome,
  CloudUpload,
  UploadOutlined,
  AccessTime,
  Search
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import {
  EngineeringDocument,
  ReferenceStandard,
  SpecAuditSessionResponse
} from "../../data/models/knowledge-agent.model";
import {
  useGetKnowledgeAgentDocumentsQuery,
  useGetReferenceStandardsQuery,
  useGetSpecAuditSessionQuery
} from "../../data/api/queries/knowledgeAgent";
import {
  useUploadReferenceStandardMutation,
  useTriggerSpecAuditSessionMutation,
  useResumeSpecAuditSessionMutation
} from "../../data/api/commands/knowledgeAgentCommands";
import "./KnowledgeAgent.scss";

const getFileExtension = (fileName: string): string => {
  const ext = fileName.split(".").pop()?.toUpperCase() || "";
  return ext;
};

const KnowledgeAgent: React.FC = () => {
  const navigate = useNavigate();

  const { data: documentsResponse, isLoading: isLoadingDocs } =
    useGetKnowledgeAgentDocumentsQuery();

  const {
    data: refStandardsResponse,
    isLoading: isLoadingRefStandards,
    isFetching: isFetchingRefStandards,
    refetch: refetchReferenceStandards
  } = useGetReferenceStandardsQuery();

  const [uploadReferenceStandard, { isLoading: isUploading }] =
    useUploadReferenceStandardMutation();

  const [triggerSpecAuditSession, { isLoading: isAnalysing }] =
    useTriggerSpecAuditSessionMutation();

  const [resumeSpecAuditSession] = useResumeSpecAuditSessionMutation();

  const engineeringDocs: EngineeringDocument[] = (
    documentsResponse?.data ?? []
  ).map((item) => ({
    id: item.documentGuid,
    documentName: item.name,
    date: new Date(item.createdDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    }),
    source: "Processing Hub"
  }));

  const referenceStandards: ReferenceStandard[] = (
    refStandardsResponse ?? []
  ).map((item) => ({
    id: item.guidCode,
    documentName: decodeURIComponent(item.fileName),
    type: getFileExtension(item.fileName),
    date: new Date(item.createdDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    }),
    source: "Uploaded"
  }));

  const [selectedEngDoc, setSelectedEngDoc] = useState<string | null>(null);
  const [selectedRefStandards, setSelectedRefStandards] = useState<Set<string>>(
    new Set()
  );
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"chat" | "prompts">("chat");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<
    { type: "user" | "assistant"; content: string }[]
  >([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [hitlResponse, setHitlResponse] = useState("");
  const [isSubmittingHitl, setIsSubmittingHitl] = useState(false);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  const {
    data: specAuditSession,
    isLoading: isLoadingSession,
    isFetching: isFetchingSession,
    refetch: refetchSpecAuditSession
  } = useGetSpecAuditSessionQuery(sessionId!, { skip: !sessionId });

  const totalParamCount = specAuditSession?.responseJson
    ? specAuditSession.responseJson.ok_count +
      specAuditSession.responseJson.under_count +
      specAuditSession.responseJson.over_count +
      specAuditSession.responseJson.in_review_count
    : 0;

  const handleEngDocSelect = (docId: string) => {
    setSelectedEngDoc(docId);
  };

  const handleRefStandardToggle = (docId: string) => {
    const newSelected = new Set(selectedRefStandards);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedRefStandards(newSelected);
  };

  const handleAnalyse = async () => {
    if (!selectedEngDoc || selectedRefStandards.size === 0 || !chatInput.trim())
      return;
    try {
      const response = await triggerSpecAuditSession({
        engDocId: selectedEngDoc,
        standardIds: Array.from(selectedRefStandards),
        userQuery: chatInput.trim()
      }).unwrap();
      setSessionId(response.sessionId);
      setShowAnalysis(true);
    } catch (error) {
      console.error("Spec audit trigger failed:", error);
    }
  };

  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    setChatMessages((prev) => [...prev, { type: "user", content: chatInput }]);
    setChatInput("");
  };

  const handleHitlSubmit = async () => {
    if (!hitlResponse.trim() || !sessionId) return;
    setIsSubmittingHitl(true);
    try {
      await resumeSpecAuditSession({
        session_id: sessionId,
        answers: [hitlResponse.trim()]
      }).unwrap();
      setHitlResponse("");
      refetchSpecAuditSession();
    } catch (error) {
      console.error("HITL submission failed:", error);
    } finally {
      setIsSubmittingHitl(false);
    }
  };

  const handleUploadBrowseClick = () => {
    uploadFileInputRef.current?.click();
  };

  const handleUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0) return;
    try {
      for (const file of uploadFiles) {
        await uploadReferenceStandard(file).unwrap();
      }
      setUploadFiles([]);
      setIsUploadModalOpen(false);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const totalSelected = (selectedEngDoc ? 1 : 0) + selectedRefStandards.size;

  const getSelectedDocNames = () => {
    const names: string[] = [];
    if (selectedEngDoc) {
      const doc = engineeringDocs.find((d) => d.id === selectedEngDoc);
      if (doc) names.push(doc.documentName);
    }
    referenceStandards.forEach((doc) => {
      if (selectedRefStandards.has(doc.id)) {
        names.push(doc.documentName);
      }
    });
    return names;
  };

  return (
    <div className="knowledge-agent-page">
      <div className="hub-breadcrumb">
        <span className="breadcrumb-link" onClick={() => navigate("/")}>
          Home
        </span>
        <span className="breadcrumb-separator">&gt;</span>
        <span
          className="breadcrumb-link"
          onClick={() => navigate("/knowledge-agent/all-details")}
        >
          Knowledge Agents
        </span>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Create Session</span>
      </div>

      <h1 className="page-title">Knowledge Agents</h1>
      <p className="page-description">
        Compare engineering specifications against standards and identify
        compliance gaps using AI.
      </p>

      {/* Stepper */}
      <div className="stepper">
        <div className="stepper-step active">
          <span className="step-number">1</span>
          <span className="step-label">SELECT ENGINEERING DOCUMENT</span>
        </div>
        <span className="stepper-arrow">→</span>
        <div className="stepper-step active">
          <span className="step-number">2</span>
          <span className="step-label">SELECT REFERENCE STANDARDS</span>
        </div>
        <span className="stepper-arrow">→</span>
        <div className="stepper-step active">
          <span className="step-number">3</span>
          <span className="step-label">ASK AI WITH QUERY</span>
        </div>
        <span className="stepper-arrow">→</span>
        <div className="stepper-step active">
          <span className="step-number">4</span>
          <span className="step-label">VIEW FINDINGS</span>
        </div>
      </div>

      <div className="panels-container">
        {/* Engineering Document Panel */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-header-content">
              <h2 className="panel-title">Engineering Document</h2>
              <p className="panel-subtitle">
                Select one engineering specification for AI comparison analysis
              </p>
            </div>
          </div>

          <div className="panel-table-wrapper">
            <table className="panel-table eng-doc-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Document Name</th>
                  <th>Date</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingDocs ? (
                  <tr>
                    <td colSpan={4}>Loading documents...</td>
                  </tr>
                ) : (
                  engineeringDocs.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <input
                          type="radio"
                          name="engineering-doc"
                          checked={selectedEngDoc === doc.id}
                          onChange={() => handleEngDocSelect(doc.id)}
                        />
                      </td>
                      <td>
                        <span className="doc-link" title={doc.documentName}>
                          {doc.documentName}
                        </span>
                      </td>
                      <td>{doc.date}</td>
                      <td>{doc.source}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reference Standards Panel */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-header-content">
              <h2 className="panel-title">Reference Standards</h2>
              <p className="panel-subtitle">
                Upload or select one or more standards for comparison.
              </p>
            </div>
            <div className="panel-header-actions">
              <button
                className={`refresh-btn${
                  isFetchingRefStandards ? " spinning" : ""
                }`}
                onClick={() => refetchReferenceStandards()}
                disabled={isFetchingRefStandards}
                title="Refresh reference standards"
              >
                <Refresh className="refresh-icon" />
              </button>
              <button
                className="upload-btn"
                onClick={() => setIsUploadModalOpen(true)}
              >
                <Upload className="upload-icon" />
                Upload
              </button>
            </div>
          </div>

          <div className="panel-table-wrapper">
            <table className="panel-table ref-std-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Document Name</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingRefStandards ? (
                  <tr>
                    <td colSpan={5}>Loading reference standards...</td>
                  </tr>
                ) : (
                  referenceStandards.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRefStandards.has(doc.id)}
                          onChange={() => handleRefStandardToggle(doc.id)}
                        />
                      </td>
                      <td>
                        <span className="doc-link" title={doc.documentName}>
                          {doc.documentName}
                        </span>
                      </td>
                      <td>{doc.type}</td>
                      <td>{doc.date}</td>
                      <td>{doc.source}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      {totalSelected > 0 && (
        <div className="action-bar">
          <div className="action-bar-top">
            <div className="action-bar-left">
              <div className="action-bar-info">
                <div className="ready-label">READY TO ANALYZE</div>
                <div className="selected-count">
                  {totalSelected} Documents
                  <span> Selected</span>
                </div>
              </div>
              <div className="selected-chips">
                {getSelectedDocNames().map((name, idx) => (
                  <span key={idx} className="chip">
                    <InsertDriveFileOutlined style={{ fontSize: 14 }} />
                    {name}
                  </span>
                ))}
              </div>
            </div>
            <p className="action-bar-hint">
              *Kindly describe what you want AI to analyse in the selected
              documents.
            </p>
          </div>
          <div className="action-bar-bottom">
            <input
              type="text"
              className="analyse-input"
              placeholder="Is the compressor operating stability adequately specified for this FPSO?"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAnalyse()}
            />
            <button
              className="analyse-btn"
              onClick={handleAnalyse}
              disabled={
                !selectedEngDoc ||
                selectedRefStandards.size === 0 ||
                !chatInput.trim() ||
                isAnalysing
              }
            >
              {isAnalysing ? "Analysing..." : "Analyse Documents"}
              <CompareArrows className="analyse-icon" />
            </button>
          </div>
        </div>
      )}

      {/* AI Chat Analysis Section */}
      {/* totalSelected > 0,   showAnalysis */}
      {showAnalysis && (
        <div className="analysis-chat-section">
          <div className="chat-tabs-header">
            <div className="chat-tabs">
              <button
                className={`chat-tab ${activeTab === "chat" ? "active" : ""}`}
                onClick={() => setActiveTab("chat")}
              >
                <AutoAwesome className="tab-icon" />
                AI Chat
              </button>
              {/* <button
                className={`chat-tab ${
                  activeTab === "prompts" ? "active" : ""
                }`}
                onClick={() => setActiveTab("prompts")}
              >
                Example Query
              </button> */}
            </div>
            <div className="chat-header-actions">
              <button
                className={`refresh-btn${isFetchingSession ? " spinning" : ""}`}
                onClick={() => refetchSpecAuditSession()}
                disabled={isFetchingSession}
                title="Refresh analysis"
              >
                <Refresh className="refresh-icon" />
                Refresh
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          {/* <div className="chat-progress-bar"></div> */}

          <div className="chat-body">
            <div className="chat-messages">
              {isLoadingSession || isFetchingSession ? (
                <div className="analysis-result">
                  <p>Analysing documents...</p>
                </div>
              ) : specAuditSession?.status === "complete" &&
                specAuditSession.responseJson ? (
                <>
                  {/* Context Card */}
                  <div className="analysis-result context-card">
                    <h4 className="section-title">Context</h4>
                    <ul className="analysis-points">
                      {specAuditSession.responseJson.context_card.asset && (
                        <li>
                          <strong>Asset:</strong>{" "}
                          {specAuditSession.responseJson.context_card.asset}
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card.system && (
                        <li>
                          <strong>System:</strong>{" "}
                          {specAuditSession.responseJson.context_card.system}
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card.subsystem && (
                        <li>
                          <strong>Subsystem:</strong>{" "}
                          {specAuditSession.responseJson.context_card.subsystem}
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .equipment_tag && (
                        <li>
                          <strong>Equipment Tag:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .equipment_tag
                          }
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .equipment_type && (
                        <li>
                          <strong>Equipment Type:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .equipment_type
                          }
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .operating_intent && (
                        <li>
                          <strong>Operating Intent:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .operating_intent
                          }
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .environment && (
                        <li>
                          <strong>Environment:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .environment
                          }
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card.location && (
                        <li>
                          <strong>Location:</strong>{" "}
                          {specAuditSession.responseJson.context_card.location}
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .equipment_service_life_years != null && (
                        <li>
                          <strong>Equipment Service Life:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .equipment_service_life_years
                          }{" "}
                          years
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .fpso_on_station_life_years != null && (
                        <li>
                          <strong>FPSO On-Station Life:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .fpso_on_station_life_years
                          }{" "}
                          years
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .mooring_type && (
                        <li>
                          <strong>Mooring Type:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .mooring_type
                          }
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .disconnection_policy && (
                        <li>
                          <strong>Disconnection Policy:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .disconnection_policy
                          }
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .topside_concept && (
                        <li>
                          <strong>Topside Concept:</strong>{" "}
                          {
                            specAuditSession.responseJson.context_card
                              .topside_concept
                          }
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card.gas_roles
                        .length > 0 && (
                        <li>
                          <strong>Gas Roles:</strong>{" "}
                          {specAuditSession.responseJson.context_card.gas_roles.join(
                            ", "
                          )}
                        </li>
                      )}
                      {specAuditSession.responseJson.context_card
                        .future_provisions.length > 0 && (
                        <li>
                          <strong>Future Provisions:</strong>{" "}
                          {specAuditSession.responseJson.context_card.future_provisions.join(
                            ", "
                          )}
                        </li>
                      )}
                    </ul>

                    {/* Raw Context Chunks */}
                    {/* {specAuditSession.responseJson.context_card
                      .raw_context_chunks.length > 0 && (
                      <div className="raw-context-section">
                        <h5 className="raw-context-title">
                          Raw Context Chunks:
                        </h5>
                        <ul className="raw-context-list">
                          {specAuditSession.responseJson.context_card.raw_context_chunks.map(
                            (chunk, idx) => (
                              <li key={idx}>{chunk}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )} */}
                  </div>

                  {/* Summary Counts */}
                  <div className="analysis-result summary-counts">
                    <h4 className="section-title">Summary</h4>
                    <div className="counts-grid">
                      <div className="count-item total">
                        <span className="count-value">
                          {totalParamCount || "No data"}
                        </span>
                        <span className="count-label">Total</span>
                      </div>
                      <div className="count-item ok">
                        <span className="count-value">
                          {specAuditSession.responseJson.ok_count}
                        </span>
                        <span className="count-label">OK</span>
                      </div>
                      <div className="count-item under">
                        <span className="count-value">
                          {specAuditSession.responseJson.under_count}
                        </span>
                        <span className="count-label">Under</span>
                      </div>
                      <div className="count-item over">
                        <span className="count-value">
                          {specAuditSession.responseJson.over_count}
                        </span>
                        <span className="count-label">Over</span>
                      </div>
                      <div className="count-item in-review">
                        <span className="count-value">
                          {specAuditSession.responseJson.in_review_count}
                        </span>
                        <span className="count-label">In Review</span>
                      </div>
                    </div>
                  </div>

                  {/* Parameter Findings */}
                  {specAuditSession.responseJson.parameter_table
                    .filter((param) => param.final_verdict !== "OK")
                    .map((param) => (
                      <div
                        key={param.param_name}
                        className={`risk-alert ${
                          param.context_severity === "HIGH"
                            ? "high-risk"
                            : param.context_severity === "MEDIUM"
                            ? "medium-risk"
                            : "low-risk"
                        }`}
                      >
                        <div className="risk-alert-header">
                          <WarningAmber className="risk-icon" />
                          <span className="risk-title">
                            {param.param_display} — {param.final_verdict} (
                            {param.context_severity} Severity)
                          </span>
                        </div>
                        <div className="risk-alert-body">
                          <p className="risk-description">
                            {param.explanation}
                          </p>
                          <div className="param-values">
                            <span className="param-value spec">
                              <strong>Spec:</strong> {param.spec_value_str}
                            </span>
                            <span className="param-value standard">
                              <strong>Standard:</strong>{" "}
                              {param.standard_value_str}
                            </span>
                            <span className="param-value action-badge">
                              Action: {param.action}
                            </span>
                            {/* {param.standard_clause && ( */}
                            <span className="param-value standard-clause">
                              <InsertDriveFileOutlined
                                style={{ fontSize: 14, marginRight: 4 }}
                              />
                              Standard Clause: {param.standard_clause}
                            </span>
                            {/* )} */}
                          </div>
                          {specAuditSession.responseJson!.explanations[
                            param.param_name
                          ] && (
                            <div className="explanation-detail">
                              <strong>Explanation:</strong>{" "}
                              {
                                specAuditSession.responseJson!.explanations[
                                  param.param_name
                                ]
                              }
                            </div>
                          )}
                          {specAuditSession.responseJson!.risks[
                            param.param_name
                          ] && (
                            <div className="risk-detail">
                              <strong>Risk:</strong>{" "}
                              {
                                specAuditSession.responseJson!.risks[
                                  param.param_name
                                ]
                              }
                            </div>
                          )}
                          {specAuditSession.responseJson!.actions.find(
                            (a) => a.param_name === param.param_name
                          ) && (
                            <div className="action-detail">
                              <strong>Recommended Action:</strong>{" "}
                              {
                                specAuditSession.responseJson!.actions.find(
                                  (a) => a.param_name === param.param_name
                                )!.recommended_next_step
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                  {/* OK Parameters */}
                  {specAuditSession.responseJson.ok_count > 0 && (
                    <div className="analysis-result ok-params">
                      <h4 className="section-title ok-title">
                        Compliant Parameters
                      </h4>
                      {specAuditSession.responseJson.parameter_table
                        .filter((param) => param.final_verdict === "OK")
                        .map((param) => (
                          <div key={param.param_name} className="ok-param-item">
                            <div className="ok-param-header">
                              <span className="ok-badge">OK</span>
                              <strong>{param.param_display}</strong>
                            </div>
                            <p className="ok-param-explanation">
                              {specAuditSession.responseJson!.explanations[
                                param.param_name
                              ] || param.explanation}
                            </p>
                            <div className="param-values">
                              <span className="param-value spec">
                                <strong>Spec:</strong> {param.spec_value_str}
                              </span>
                              <span className="param-value standard">
                                <strong>Standard:</strong>{" "}
                                {param.standard_value_str}
                              </span>
                            </div>
                            {specAuditSession.responseJson!.risks[
                              param.param_name
                            ] && (
                              <p className="ok-param-risk">
                                <strong>Note:</strong>{" "}
                                {
                                  specAuditSession.responseJson!.risks[
                                    param.param_name
                                  ]
                                }
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </>
              ) : specAuditSession?.status === "error" ? (
                <div className="analysis-error">
                  <WarningAmber className="error-icon" />
                  <p>Error in analysis. Please trigger the procedure again.</p>
                </div>
              ) : specAuditSession?.status === "hitl_required" &&
                specAuditSession.hitlQuestions ? (
                <div className="hitl-clarification-section">
                  <div className="hitl-card">
                    <div className="hitl-card-header">
                      <span className="hitl-icon">&#9432;</span>
                      <span className="hitl-title">
                        AGENT CLARIFICATION REQUEST
                      </span>
                      <span className="hitl-badge">
                        {Array.isArray(specAuditSession.hitlQuestions)
                          ? specAuditSession.hitlQuestions.length
                          : 0}{" "}
                        {Array.isArray(specAuditSession.hitlQuestions) &&
                        specAuditSession.hitlQuestions.length === 1
                          ? "Question"
                          : "Questions"}
                      </span>
                    </div>
                    <div className="hitl-card-body">
                      <p className="hitl-description">
                        To refine the compliance assessment, I need a few
                        clarifications. Your answers will help me re-evaluate
                        the verdict with greater precision.
                      </p>
                      <div className="hitl-questions-list">
                        {(Array.isArray(specAuditSession.hitlQuestions)
                          ? specAuditSession.hitlQuestions
                          : []
                        ).map(
                          (
                            item: { question: string; context: string },
                            idx: number
                          ) => (
                            <div key={idx} className="hitl-question-card">
                              <div className="hitl-question-content">
                                <p className="hitl-question-text">
                                  {item.question}
                                </p>
                                {item.context && (
                                  <div className="hitl-question-context">
                                    <span className="context-label">
                                      Why this matters:
                                    </span>
                                    <p className="context-text">
                                      {item.context}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="hitl-response-input-section">
                    <div className="hitl-input-label">
                      <Send style={{ fontSize: 14, color: "#656579" }} />
                      <span>
                        Provide your answers below to continue the analysis
                      </span>
                    </div>
                    <div className="hitl-input-row">
                      <textarea
                        className="hitl-response-input"
                        placeholder="Type your response here... (e.g., Yes, a sour-service assessment has been conducted confirming H2S levels support 24 HRC)"
                        value={hitlResponse}
                        onChange={(e) => setHitlResponse(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleHitlSubmit();
                          }
                        }}
                        disabled={isSubmittingHitl}
                        rows={3}
                      />
                      <button
                        className="hitl-send-btn"
                        onClick={handleHitlSubmit}
                        disabled={!hitlResponse.trim() || isSubmittingHitl}
                        title="Send response"
                      >
                        {isSubmittingHitl ? (
                          <CircularProgress
                            size={18}
                            style={{ color: "#fff" }}
                          />
                        ) : (
                          <Send className="hitl-send-icon" />
                        )}
                      </button>
                    </div>
                    <p className="hitl-input-hint">
                      Press Enter to send · Shift+Enter for new line
                    </p>
                  </div>
                </div>
              ) : specAuditSession?.status &&
                specAuditSession.status !== "complete" ? (
                <div className="analysis-loading">
                  <CircularProgress size={40} />
                  <p>Analysis in progress...</p>
                  <p>Please click the refresh button to update the status.</p>
                </div>
              ) : null}

              {/* User messages */}
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`chat-message ${msg.type}`}>
                  {msg.content}
                </div>
              ))}

              {/* Recommended Questions */}
              {/* <div className="recommended-questions">
                <div className="recommended-header">
                  <Search className="recommended-icon" />
                  <span>Recommended Questions</span>
                </div>
              </div> */}
            </div>
          </div>

          {/* <div className="chat-footer">
            <p className="chat-disclaimer">
              Exercise professional judgement while reviewing AI responses as
              they may be inaccurate.
            </p>
          </div> */}
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div
          className="upload-modal-overlay"
          onClick={() => setIsUploadModalOpen(false)}
        >
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3>Upload Reference Standards</h3>
              <button
                className="modal-close-btn"
                onClick={() => setIsUploadModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="upload-modal-body">
              <div
                className={`upload-dropzone${
                  uploadFiles.length > 0 ? " has-files" : ""
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add("drag-over");
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove("drag-over");
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove("drag-over");
                  const droppedFiles = Array.from(e.dataTransfer.files).filter(
                    (file) => file.size > 0 || file.type !== ""
                  );
                  if (droppedFiles.length > 0) {
                    setUploadFiles((prev) => [...prev, ...droppedFiles]);
                  }
                }}
                onClick={handleUploadBrowseClick}
              >
                <div className="dropzone-icon">
                  <CloudUpload className="cloud-upload-icon" />
                </div>
                <p className="dropzone-text">
                  Drag and drop files or folders here or{" "}
                  <span className="browse-link">browse</span>
                </p>
                <p className="dropzone-max-size">
                  Supported file types include .jpg, .jpeg, .png, .csv, .xls,
                  .xlsx, .doc, .docx, .pdf, .txt, .ppt, .pptx
                </p>
              </div>

              <button
                className="upload-attachment-btn"
                onClick={handleUploadSubmit}
                disabled={uploadFiles.length === 0 || isUploading}
              >
                <UploadOutlined style={{ fontSize: 18, marginRight: 6 }} />
                {isUploading ? "Uploading..." : "Upload Attachment"}
              </button>

              <input
                ref={uploadFileInputRef}
                type="file"
                multiple
                onChange={handleUploadFileChange}
                style={{ display: "none" }}
              />

              {uploadFiles.length > 0 && (
                <div className="selected-files-list">
                  <p>{uploadFiles.length} file(s) selected</p>
                  {uploadFiles.map((file, idx) => (
                    <div key={idx} className="selected-file-item">
                      <InsertDriveFileOutlined style={{ fontSize: 16 }} />
                      <span>{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeAgent;
